import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { PackageSearch, Loader2, Check, Truck, ExternalLink, MessageCircle, AlertCircle, Clock, ChevronRight } from "lucide-react";
import { waUrl } from "@/lib/brand";
import { useLenis } from "@/hooks/useLenis";
import { useSeo } from "@/lib/seo";
import { graph, webPageLd } from "@/lib/schema";
import { Footer } from "@/components/site/Footer";
import { FloatingWhatsApp } from "@/components/site/FloatingWhatsApp";
import { Breadcrumbs, breadcrumbJsonLd } from "@/components/site/Breadcrumbs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inputCls = "w-full px-4 py-3.5 rounded-xl bg-[#FBF4E1] border border-[#8D6E63]/15 focus:border-[#2E7D32] focus:ring-2 focus:ring-[#2E7D32]/20 outline-none transition text-base";
const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : null;

const Timeline = ({ steps }) => (
  <ol className="relative" data-testid="track-timeline">
    {steps.map((s, i) => (
      <motion.li key={s.key} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.08 }} className="relative flex gap-4 pb-6 last:pb-0" data-testid={`track-step-${s.key}`} data-done={s.done} data-current={s.current}>
        {i < steps.length - 1 && <span className={`absolute left-[15px] top-8 bottom-0 w-0.5 ${steps[i + 1].done ? "bg-[#2E7D32]" : "bg-[#8D6E63]/20"}`} />}
        <span className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center shrink-0 border-2 ${s.done ? "bg-[#2E7D32] border-[#2E7D32]" : "bg-white border-[#8D6E63]/30"} ${s.current && s.done ? "ring-4 ring-[#2E7D32]/20" : ""}`}>
          {s.done ? <Check className="w-4 h-4 text-white" strokeWidth={3} /> : <span className="w-2 h-2 rounded-full bg-[#8D6E63]/40" />}
        </span>
        <div className="pt-1">
          <div className={`font-semibold ${s.done ? "text-[#143C18]" : "text-[#8D6E63]"}`}>{s.label}</div>
          {s.current && s.done && <div className="text-xs text-[#2E7D32] font-semibold mt-0.5">Current status</div>}
        </div>
      </motion.li>
    ))}
  </ol>
);

const Result = ({ order }) => (
  <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="card-premium !transform-none p-6 sm:p-8 space-y-6" data-testid="track-result">
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div>
        <div className="text-[11px] uppercase tracking-[0.25em] text-[#8D6E63] font-bold">Order</div>
        <div className="font-display text-2xl sm:text-3xl font-bold text-[#143C18]" data-testid="track-order-number">{order.order_number}</div>
        <div className="text-sm text-[#5D4037] mt-1">Placed on {fmtDate(order.order_date)}{order.destination ? ` · ${order.destination}` : ""}</div>
      </div>
      <div className="chip">{(order.fulfillment_status || "").replace(/_/g, " ").toLowerCase() || "confirmed"}</div>
    </div>

    <Timeline steps={order.timeline} />

    {order.fulfillments.length > 0 ? (
      <div className="space-y-3" data-testid="track-shipments">
        {order.fulfillments.map((f, i) => (
          <div key={i} className="p-4 rounded-2xl bg-[#FBF4E1] border border-[#8D6E63]/15">
            <div className="flex items-center gap-2 font-semibold text-[#143C18]"><Truck className="w-4 h-4 text-[#2E7D32]" /> Shipment {order.fulfillments.length > 1 ? i + 1 : ""}</div>
            <dl className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              {f.courier && <><dt className="text-[#8D6E63]">Courier</dt><dd className="font-semibold text-[#143C18] break-words">{f.courier}</dd></>}
              {f.tracking_number && <><dt className="text-[#8D6E63]">Tracking number</dt><dd className="font-mono font-semibold text-[#143C18] break-all" data-testid="track-tracking-number">{f.tracking_number}</dd></>}
              {f.shipped_at && <><dt className="text-[#8D6E63]">Shipped on</dt><dd className="font-semibold text-[#143C18]">{fmtDate(f.shipped_at)}</dd></>}
              {f.estimated_delivery && <><dt className="text-[#8D6E63]">Estimated delivery</dt><dd className="font-semibold text-[#143C18]">{fmtDate(f.estimated_delivery)}</dd></>}
              {f.delivered_at && <><dt className="text-[#8D6E63]">Delivered on</dt><dd className="font-semibold text-[#143C18]">{fmtDate(f.delivered_at)}</dd></>}
            </dl>
            {f.tracking_url && <a href={f.tracking_url} target="_blank" rel="noopener noreferrer" data-testid="track-courier-link" className="btn-outline !py-3 w-full sm:w-auto justify-center mt-4">Track with courier <ExternalLink className="w-4 h-4" /></a>}
          </div>
        ))}
      </div>
    ) : (
      <div className="p-4 rounded-2xl bg-[#D4A017]/10 border border-[#D4A017]/30 text-sm text-[#5D4037] flex gap-3" data-testid="track-no-tracking">
        <Clock className="w-5 h-5 text-[#B8860B] shrink-0" />
        <span>Your order has been received, but tracking information is not available yet. Please check again after your order has been dispatched.</span>
      </div>
    )}

    <div>
      <div className="text-[11px] uppercase tracking-[0.25em] text-[#8D6E63] font-bold mb-2">Items</div>
      <ul className="divide-y divide-[#8D6E63]/12" data-testid="track-items">
        {order.items.map((it) => <li key={it.title} className="py-2 flex justify-between gap-4 text-sm"><span className="text-[#143C18]">{it.title}</span><span className="text-[#8D6E63] shrink-0">× {it.quantity}</span></li>)}
      </ul>
    </div>
  </motion.div>
);

export default function TrackOrderPage() {
  useLenis();
  const [form, setForm] = useState({ order_number: "", contact: "" });
  const [state, setState] = useState({ status: "idle" });
  const [configured, setConfigured] = useState(null);
  const crumbs = [{ label: "Home", to: "/" }, { label: "Track Order" }];

  useSeo({
    title: "Track Your Order | Rudrima",
    description: "Track your Rudrima order and check the latest delivery or shipping status using your order details.",
    path: "/track-order",
    jsonLd: graph(webPageLd({ path: "/track-order", name: "Track Your Order | Rudrima", description: "Check the status of your Rudrima order." }), breadcrumbJsonLd(crumbs)),
  });

  useEffect(() => { axios.get(`${API}/track-order/status`).then((r) => setConfigured(r.data.configured)).catch(() => setConfigured(false)); }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    const num = form.order_number.trim();
    const contact = form.contact.trim();
    if (!num) return setState({ status: "error", message: "Please enter your order number." });
    if (!(contact.includes("@") ? /^\S+@\S+\.\S+$/.test(contact) : contact.replace(/\D/g, "").length >= 10)) {
      return setState({ status: "error", message: "Please enter the email address or 10-digit mobile number used for the order." });
    }
    setState({ status: "loading" });
    try {
      const { data } = await axios.post(`${API}/track-order`, { order_number: num, contact });
      setState({ status: "found", order: data });
    } catch (err) {
      const code = err.response?.data?.detail?.code;
      if (code === "not_found") setState({ status: "not_found" });
      else if (code === "not_configured") setState({ status: "not_configured" });
      else setState({ status: "error", message: err.response?.data?.detail?.message || "Something went wrong. Please try again." });
    }
  };

  return (
    <>
      <main data-testid="track-order-page">
        <section className="relative pt-24 sm:pt-32 lg:pt-36 pb-10 lg:pb-14 bg-hero-gradient grain overflow-hidden">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 relative text-center">
            <Breadcrumbs items={crumbs} className="mb-5 justify-center [&>ol]:justify-center" />
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-14 h-14 mx-auto rounded-2xl bg-[#143C18] flex items-center justify-center mb-5"><PackageSearch className="w-7 h-7 text-[#E8BE4A]" /></motion.div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-[#143C18] leading-[1.05] tracking-tight" data-testid="track-title">Track Your Order</h1>
            <p className="mt-4 text-base sm:text-lg text-[#5D4037] leading-relaxed">Enter your order details below to check the latest status of your Rudrima order.</p>
          </div>
        </section>

        <section className="py-10 sm:py-14 bg-[#FFF8E7]">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-10 space-y-6">
            <form onSubmit={onSubmit} noValidate className="card-premium !transform-none p-6 sm:p-8" data-testid="track-form" aria-describedby="track-help">
              <h2 className="font-display text-2xl font-bold text-[#143C18] mb-5">Track your Rudrima order</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="order_number" className="text-xs uppercase tracking-widest text-[#8D6E63] font-bold mb-2 block">Order Number</label>
                  <input id="order_number" value={form.order_number} onChange={(e) => setForm({ ...form, order_number: e.target.value })} data-testid="track-order-input" className={inputCls} placeholder="e.g. #1024" autoComplete="off" inputMode="text" />
                </div>
                <div>
                  <label htmlFor="contact" className="text-xs uppercase tracking-widest text-[#8D6E63] font-bold mb-2 block">Email or Mobile Number</label>
                  <input id="contact" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} data-testid="track-contact-input" className={inputCls} placeholder="you@example.com or 98xxxxxxxx" autoComplete="email" />
                </div>
              </div>
              <p id="track-help" className="mt-3 text-xs text-[#8D6E63]">Use the same email or mobile number you entered at checkout. Your order number is in your Shopify confirmation email/SMS.</p>
              <button type="submit" disabled={state.status === "loading"} data-testid="track-submit-btn" className="btn-primary w-full justify-center !py-4 text-base mt-5 disabled:opacity-60">
                {state.status === "loading" ? <><Loader2 className="w-5 h-5 animate-spin" /> Checking your order status...</> : <><PackageSearch className="w-5 h-5" /> Track Order</>}
              </button>
            </form>

            <div aria-live="polite" role="status">
              {state.status === "error" && (
                <div className="p-4 rounded-2xl bg-red-50 border border-red-200 text-red-800 text-sm flex gap-3" data-testid="track-error"><AlertCircle className="w-5 h-5 shrink-0" /> {state.message}</div>
              )}
              {state.status === "not_found" && (
                <div className="p-5 rounded-2xl bg-white border border-[#8D6E63]/15 text-[#5D4037] text-sm flex gap-3" data-testid="track-not-found"><AlertCircle className="w-5 h-5 text-[#B8860B] shrink-0" /> We couldn't find an order with those details. Please check your order number and contact information and try again.</div>
              )}
              {(state.status === "not_configured" || (configured === false && state.status === "idle")) && (
                <div className="p-5 rounded-2xl bg-[#D4A017]/10 border border-[#D4A017]/30 text-sm text-[#5D4037]" data-testid="track-not-configured">
                  <div className="font-semibold text-[#143C18] mb-1 flex items-center gap-2"><Clock className="w-4 h-4 text-[#B8860B]" /> Live tracking is being set up</div>
                  Online order lookup is not connected yet. In the meantime, use the order status link in your Shopify confirmation email, or message us on WhatsApp with your order number and we'll share your delivery status.
                  <a href={waUrl("Hi Rudrima! Please share the delivery status of my order. Order Number: ")} target="_blank" rel="noopener noreferrer" data-testid="track-whatsapp-btn" className="btn-primary !bg-[#25D366] hover:!bg-[#1DA851] !py-3 w-full sm:w-auto justify-center mt-4"><MessageCircle className="w-4 h-4" /> Ask on WhatsApp</a>
                </div>
              )}
              {state.status === "found" && <Result order={state.order} />}
            </div>

            <p className="text-center text-sm text-[#8D6E63]">Need help? <Link to="/contact-us" className="text-[#2E7D32] font-semibold hover:underline inline-flex items-center gap-1">Contact support <ChevronRight className="w-3.5 h-3.5" /></Link> · <Link to="/shipping-policy" className="hover:text-[#2E7D32] underline-offset-2 hover:underline">Shipping Policy</Link></p>
          </div>
        </section>
      </main>
      <Footer bottomPad="pb-10" />
      <FloatingWhatsApp />
    </>
  );
}
